import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Textarea, Badge } from '../../../shared/components';
import { SearchSelect } from '../../../shared/components/SearchSelect';
import { TimerIcon, PlayIcon, CloseIcon } from '../../../shared/icons';
import { useOperationTimer } from '../hooks/useOperationTimer';
import { CancelActivityModal } from './CancelActivityModal';
import { ParamActivity } from '../../../shared/services/settings/types';
import { LccUser } from '../../../shared/auth/types';

interface ActivityFormProps {
  user: LccUser;
  activities: ParamActivity[];
  isSaving: boolean;
  onSave: (data: { activityId: string; activityName: string; startDateTime: string; observations?: string; pausedSeconds?: number }) => Promise<void>;
}

function formatClock(iso: string) {
  return new Date(iso).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
function formatElapsed(totalSeconds: number) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

/**
 * Formulario principal de Control de Tiempos Operativos.
 * - Hora de inicio: SOLO arranca cuando el usuario da clic en "Iniciar
 *   actividad" (antes arrancaba sola al abrir la pantalla - ver bug fix en
 *   useOperationTimer.ts). El mismo boton sirve para reiniciar a mitad de
 *   camino si el usuario lo necesita.
 * - Usuario: tomado automaticamente del usuario autenticado (no se selecciona manualmente).
 * - Guardar: deshabilitado mientras no se haya iniciado el cronometro, para
 *   que no se pueda registrar una actividad sin tiempo real corriendo.
 * - Al guardar, el cronometro vuelve a "sin iniciar" (en cero) - NO se
 *   reinicia automaticamente, hay que darle "Iniciar actividad" de nuevo
 *   para la siguiente actividad.
 * - "Pausar/Despausar Tiempo" fue RETIRADO: permitia pausas silenciosas sin
 *   rastro de duracion ni motivo (punto ciego para auditoria). En su lugar
 *   ahora esta "Cancelar Actividad": pide confirmacion, descarta el
 *   cronometro y la actividad/observaciones en curso, y regresa a Inicio
 *   (resumen del dia). No queda ningun registro a medias.
 */
export function ActivityForm({ user, activities, isSaving, onSave }: ActivityFormProps) {
  const navigate = useNavigate();
  const timer = useOperationTimer(user.id);
  const [activityId, setActivityId] = useState('');
  const [observations, setObservations] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    setError(null);
  }, [activityId]);

  const activityOptions = activities.map((a) => ({ value: a.id, label: a.name }));

  const handleSave = async () => {
    if (!timer.isRunning || !timer.startDateTime) {
      setError('Debes darle clic a "Iniciar actividad" antes de guardar.');
      return;
    }
    if (!activityId) {
      setError('Selecciona una actividad para continuar.');
      return;
    }
    const activity = activities.find((a) => a.id === activityId);
    await onSave({
      activityId,
      activityName: activity?.name ?? '',
      startDateTime: timer.startDateTime,
      observations: observations.trim() || undefined,
    });
    setActivityId('');
    setObservations('');
    timer.stop();
  };

  const handleConfirmCancel = () => {
    timer.stop();
    setActivityId('');
    setObservations('');
    setError(null);
    setShowCancelModal(false);
    navigate('/');
  };

  return (
    <Card className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-500 text-sm font-bold text-white">
            {user.fullName.split(' ').slice(0, 2).map((w) => w[0]).join('')}
          </div>
          <div>
            <p className="text-sm font-semibold text-ink-900">{user.fullName}</p>
            <p className="text-xs text-ink-400">{timer.isRunning ? 'Registrando actividad' : 'Sin iniciar'}</p>
          </div>
        </div>
        <TimerIcon className={timer.isRunning ? 'text-brand-500' : 'text-ink-300'} />
      </div>

      <div className={['flex items-center justify-between rounded-xl px-4 py-3', timer.isRunning ? 'bg-brand-light' : 'bg-ink-50'].join(' ')}>
        <div>
          <p className={['text-xs font-medium', timer.isRunning ? 'text-brand-700' : 'text-ink-500'].join(' ')}>Hora de inicio</p>
          <p className={['text-lg font-bold tabular-nums', timer.isRunning ? 'text-brand-800' : 'text-ink-400'].join(' ')}>
            {timer.startDateTime ? formatClock(timer.startDateTime) : '--:--:--'}
          </p>
        </div>
        <div className="text-right">
          <p className={['text-xs font-medium', timer.isRunning ? 'text-brand-700' : 'text-ink-500'].join(' ')}>Transcurrido</p>
          <div className="flex items-center justify-end gap-2">
            {!timer.isRunning && <Badge tone="neutral">Sin iniciar</Badge>}
            <p className={['text-lg font-bold tabular-nums', timer.isRunning ? 'text-brand-800' : 'text-ink-400'].join(' ')}>
              {formatElapsed(timer.elapsedSeconds)}
            </p>
          </div>
        </div>
      </div>

      <SearchSelect
        label="Actividad"
        placeholder="Selecciona o busca una actividad"
        options={activityOptions}
        value={activityId}
        onChange={setActivityId}
        error={error ?? undefined}
      />

      <Textarea label="Observaciones (opcional)" placeholder="Comentarios adicionales sobre la actividad..." value={observations} onChange={(e) => setObservations(e.target.value)} />

      <div className="flex flex-wrap gap-3">
        <Button variant="outline" leftIcon={<PlayIcon width={18} height={18} />} onClick={timer.start} type="button">
          Iniciar actividad
        </Button>
        <Button
          variant="outline"
          leftIcon={<CloseIcon width={18} height={18} />}
          onClick={() => setShowCancelModal(true)}
          type="button"
          disabled={!timer.isRunning}
          className="text-critical border-critical/30 hover:bg-red-50"
        >
          Cancelar Actividad
        </Button>
      </div>

      <p className="text-center text-xs font-medium leading-relaxed text-amber-700 bg-amber-50 rounded-xl px-3 py-2">
        ⚠️ Da clic en "Iniciar actividad" para empezar a contar el tiempo (también úsalo si quieres reiniciar a mitad de camino).
        <br />
        Sin iniciar, no podrás guardar el registro.
        <br />
        Al guardar, el tiempo vuelve a cero — vuelve a iniciarlo para la siguiente actividad.
      </p>

      <Button fullWidth onClick={handleSave} isLoading={isSaving} disabled={!timer.isRunning}>
        Guardar
      </Button>

      <CancelActivityModal open={showCancelModal} onClose={() => setShowCancelModal(false)} onConfirm={handleConfirmCancel} />
    </Card>
  );
}
